/* The index on the left of `/zadania`. Since direction B2C (owner, 5 November 2026) it lies on the
 * page with no ground or frame of its own: underline scope tabs, roomy rounded rows instead of
 * hairline-separated ones, and the selected row lifted as the one card in the list.
 *
 * The rows never move: a handled row is struck through IN PLACE (see `model/taskDeck`), so the list
 * has the same length and the same order from the first answer to the last. */

/* The first row of the page's grid: the index ends on the card's bottom edge, and the decisions
 * stand under the card in the desk's column only (`tasks.module.css`). */
.queue {
  grid-row: 1;
  grid-column: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: transparent;
  border: 0;
  border-radius: 0;
}

.head {
  position: relative;
  flex: none;

  /* The canon's `.queue .qh`: 18px above, 14px under; B2C's 12px at the sides. */
  padding: var(--space-5) var(--space-3) var(--space-14px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.headTitle {
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-title);
  color: var(--text);
}

/* The figure sits in a fixed track to the left of the `⋯`, so a count that changes from 0 to 14
 * when the read lands cannot move the title beside it. */
.headCount {
  position: absolute;
  top: 20px;
  right: 58px;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  font-variant-numeric: tabular-nums;
  color: var(--text-3);
}

.more {
  position: absolute;
  top: 13px;
  right: var(--space-4);
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  color: var(--text-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-control);
}

.more:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.more:hover {
  color: var(--text);
  background: var(--bg-inset);
}

.moreGlyph {
  width: 16px;
  height: 16px;
}

/* The progress meter. A `<div>` with `role="progressbar"` rather than a `<progress>`: the native
 * element cannot be styled to the canon's 6px bar in every engine and brings a default appearance
 * that differs per platform. The figure above it says the same thing in words. */
.meter {
  height: 6px;

  /* The canon's own 10px between the title and the bar. */
  margin-top: var(--space-10px);
  overflow: hidden;
  background: var(--bg-sunken);
  border-radius: var(--radius-hair);
}

.meterFill {
  height: 100%;
  background: var(--text);
  border-radius: var(--radius-hair);
}

/* ── The scope switch ──────────────────────────────────────────────────────────────────────── */

/* A RADIO GROUP, not a tablist — `pages/plan-library/ui/ScopeSwitch` states the rule and this
 * follows it: arrows move the selection inside a radio group, which is the platform contract, and
 * `useTaskShortcuts` is inert inside one for exactly that reason. Drawn as underline tabs (B2C): a
 * hairline under the three, a 2px ink rule under the chosen one. */
.scope {
  display: flex;
  gap: var(--space-1px);
  min-inline-size: var(--space-0);
  margin: var(--space-3) var(--space-0) var(--space-0);
  padding: var(--space-0);
  background: transparent;
  border: 0;
  border-bottom: var(--border-hairline) solid var(--border);
  border-radius: 0;
}

.scopeOption {
  /* The gap is SIX pixels, the mock's own, and it is the whole of the "Zadania11" the owner
   * reported: at 1.5px the count touched the word and the two read as one token. It is a gap, not a
   * missing space — the string carries one and always did. */
  display: inline-flex;
  flex: 1;
  gap: var(--space-6px);
  align-items: center;
  justify-content: center;
  height: 28px;

  /* `.scope button` is 12.5px in the canon; `--fs-note` is the repo's 12.5. */
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  border-radius: 0;
}

.scopeSelected {
  color: var(--text);
  box-shadow: var(--ring-tab-selected);
}

/* The radio itself is clipped rather than hidden: `display: none` takes it out of the tab order and
 * out of the accessibility tree, and the label's own box is then a control nothing can reach. */
.scopeInput {
  position: absolute;
  width: var(--space-1px);
  height: var(--space-1px);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.scopeWord {
  font-weight: var(--fw-medium);
  white-space: nowrap;
}

.scopeSelected .scopeWord {
  font-weight: var(--fw-semibold);
}

.scopeInput:focus-visible + .scopeWord {
  border-radius: var(--radius-hair);
  box-shadow: var(--ring-menu);
}

.scopeCount {
  font-weight: var(--fw-medium);
  font-variant-numeric: tabular-nums;
  color: var(--text-3);
  opacity: 0.7;
}

.scopeSelected .scopeCount {
  color: var(--text-2);
}

/* ── The rows ──────────────────────────────────────────────────────────────────────────────── */

/* `align-content: start` so a short deck keeps its rows at their own height instead of the grid
 * stretching them to fill the column. */
.list {
  display: grid;
  flex: 1;
  gap: var(--space-1);
  align-content: start;
  min-height: 0;
  padding: var(--space-1) var(--space-0) var(--space-0);
  margin: var(--space-0);
  overflow-y: auto;
  list-style: none;
}

.row {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  width: 100%;
  min-height: 64px;
  padding: var(--space-13px) var(--space-4);
  text-align: left;
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
}

.row:hover {
  background: var(--bg-inset);
}

/* The selected row: the one lifted card in the list (B2C) — the card ground, its shadow and a
 * hairline edge, so it stands apart from the hover's inset fill without a coloured stripe.
 * `aria-current` carries the selection for assistive tech. Two classes, so it outranks
 * `.row:hover`. */
.row.rowSelected {
  background: var(--bg-card);
  box-shadow: var(--ring-lifted-card);
}

/* The keyboard ring lands just inside the rounded row, so it follows the row's own corners. */
.row:focus-visible {
  outline: var(--border-focus) solid var(--focus);

  /* `--space-minus-2px` is the negative of `--border-focus` (2px): the ring lands just inside
   * the row instead of straddling the hairline above it. */
  outline-offset: var(--space-minus-2px);
}

/* `ReasonTile` at the index's own 28px and 8px corner. Scoped under the row so it outranks the
 * tile's size class by specificity rather than by which stylesheet chunk loads last. */
.row .tile {
  --tile-size: 28px;
  --mark-size: 13px;

  margin-right: var(--space-1);
  border-radius: var(--radius-8px);
}

/* The mark's own box inside the tile, so `markIn`'s scale has something to scale that is not the
 * tile's own ground. */
.tileSwap {
  display: grid;
  place-items: center;
}

.tileMark {
  width: 13px;
  height: 13px;
}

 /* `min-width: 0` is what lets the two lines ellipsise instead of pushing the age off the row: a
  * flex item's default `min-width: auto` refuses to shrink below its content, so the long reason
  * ran under the date. */
.rowText {
  flex: 1;
  min-width: 0;
}

/* BLOCK, not the default inline. As spans they shared one line box — "Marek NowakOpuszczony
 * trening" — and `text-overflow` does nothing to an inline box, so nothing clipped either. */
.name {
  display: block;
  overflow: hidden;
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-name);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
}

.reason {
  display: block;
  margin-top: var(--space-1px);
  overflow: hidden;
  font-size: var(--type-control);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-2);
}

.age {
  flex: none;
  font-size: var(--fs-label);
  font-weight: var(--fw-regular);
  font-variant-numeric: tabular-nums;
  color: var(--text-3);
}

/* A row with an answer on it. The strike-through is the SECOND channel beside the tile's mark and
 * the ink: `--text-3` alone would encode it in colour, and this is a state a reader has to be able
 * to tell. The row stays where it is — that is the whole design. */
.rowHandled .name,
.rowHandled .reason {
  color: var(--text-3);
  text-decoration: line-through;
}

.rowHandled .tile {
  color: var(--text-3);
  background: var(--bg-chip);
}

/* ── The foot ──────────────────────────────────────────────────────────────────────────────── */

/* Only drawn while a decision is held, which is the only time there is anything to undo. It carries
 * its own top rule rather than a reserved height: it stands at the BOTTOM of a column the card's
 * height fixes, so it grows into the list's scroller and moves nothing on the page. */
.foot {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: flex-end;
  padding: var(--space-2) var(--space-10px);
  border-top: var(--border-hairline) solid var(--border);
}

/* The ghost skin's B2C weight and ink. Doubled with the row so it outranks `shared/ui/ghostButton`
 * by specificity rather than by which stylesheet chunk lands last. */
.foot .undo {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--type-control);
  font-weight: var(--fw-medium);
  color: var(--text);
}

.undoGlyph {
  width: 14px;
  height: 14px;
}

/* ── The loading reserve ───────────────────────────────────────────────────────────────────── */

/* The row's own box, so the bars stand where the rows will. */
.skeletonRow {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-height: 64px;
  padding: var(--space-13px) var(--space-4);
}

.barTile {
  width: 28px;
  height: 28px;
  margin-right: var(--space-1);
  border-radius: var(--radius-8px);
}

.barName {
  display: block;
  width: 118px;
  height: 13px;
  border-radius: var(--radius-hair);
}

.barReason {
  display: block;
  width: 84px;
  height: 11px;
  margin-top: var(--space-10px);
  border-radius: var(--radius-hair);
}

/* The `Z` on the undo control, the same key treatment the page foot's legend draws. */
.key {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: var(--space-0) var(--space-5px);
  font-size: var(--fs-tag);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-5px);
}

/* ── The chat glyph beside a row (chat canon, board P1) ────────────────────────────────────────
 * A sibling of the row's button, laid over its trailing edge: shown on hover, on the selected row,
 * on keyboard focus, and held lit on the row whose conversation the panel is showing. The row keeps
 * room for it so the age is never under it. */
.rowItem {
  --row-chat-shown: 0;
  --row-chat-ink: var(--text-3);
  --row-chat-fill: transparent;

  position: relative;
}

.rowItem:hover,
.rowItem:focus-within,
.rowItemSelected {
  --row-chat-shown: 1;
}

.rowItemTalking {
  --row-chat-shown: 1;
  --row-chat-ink: var(--text);
  --row-chat-fill: var(--bg-chip);
}

.rowWithChat {
  padding-right: var(--space-12);
}

.rowChat {
  position: absolute;
  top: 50%;
  right: var(--space-2);
  translate: 0 -50%;
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  color: var(--row-chat-ink);
  background: var(--row-chat-fill);
  border: 0;
  border-radius: var(--radius-mark);
  cursor: pointer;
  opacity: var(--row-chat-shown);
  transition: opacity var(--dur-fast);
}

.rowChat:hover {
  color: var(--text);
  background: var(--bg-chip);
}

.rowChat:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}
